/**
 * Revision derivations the FRONTEND owns (infra C.7: "ranking suppliers 'history first' is
 * FE from the purchase-history payload").
 *
 * The point of the ranking: when the customer adds an item after the quotation went out,
 * waiting for a fresh RFQ costs days. Any supplier we have quoted this item to before —
 * on *any* requirement — already has a price we can stand behind, so those come first and
 * the purchaser can decide immediately. Suppliers linked to the item but never quoted are
 * still listed, because sometimes a fresh RFQ is the right call.
 */

import type { Item, Money, PurchaseHistoryEntry, RevisionRequest, RevisionType } from './types'
import type { BadgeTone } from './format'

/** The label and badge tone for a request row — derived from `type`, not sent by the API. */
export const REVISION_LABELS: Record<RevisionType, { title: string; tone: BadgeTone }> = {
  reprice: { title: 'Revise rate', tone: 'warn' },
  add: { title: 'Add item', tone: 'info' },
  set_qty: { title: 'Change qty', tone: 'violet' },
  remove: { title: 'Remove item', tone: 'muted' },
}

/** A supplier option for an `add` request, with whatever we already know about their price. */
export interface SupplierOption {
  supplier: string
  /** The last price we have from them for this item, on any requirement. */
  lastPrice: Money | null
  ref: string | null
  date: string | null
  /**
   * On-hand stock for the item; the master's figure, not per-supplier. `null` when the master has
   * none recorded — which, against Quoflow, is always: `Item.on_hand` is optional and never
   * populated (see `src/lib/api/mappers/item.ts`). Fix round (final whole-branch review, finding
   * 3): this used to fall back to `0`, and the one consumer, `features/revisions/
   * SupplierChoiceModal.tsx`, rendered a red "none on hand" for every row on the strength of that
   * fabricated zero — asserting a stock-out this mock never actually observed. `null` propagates
   * the absence instead of a value, so the modal can render nothing rather than a lie.
   */
  stock: number | null
  /** True when a past price means no fresh RFQ is needed. */
  hasHistory: boolean
}

/**
 * Suppliers for an item, those with a past price first (most recent first within that
 * group), then connected suppliers with no history, alphabetically.
 */
export function rankSuppliersByHistory(
  item: Item | undefined,
  history: PurchaseHistoryEntry[] | undefined,
): SupplierOption[] {
  if (!item) return []

  const latestBySupplier = new Map<string, PurchaseHistoryEntry>()
  for (const entry of history ?? []) {
    // Fail open: §4's response example omits `item`, so a backend that follows it literally
    // sends entries without one. Dropping those would leave the modal at "0 of N" forever;
    // the request is already scoped to this item by `?item=`.
    if (entry.item && entry.item !== item.code) continue
    const existing = latestBySupplier.get(entry.supplier)
    if (!existing || entry.date > existing.date) latestBySupplier.set(entry.supplier, entry)
  }

  // Every supplier we could buy from: those linked to the item, plus anyone who has quoted
  // it before even if they are no longer on the item's candidate list.
  const suppliers = [...new Set([...item.candidate_suppliers, ...latestBySupplier.keys()])]

  return suppliers
    .map((supplier): SupplierOption => {
      const entry = latestBySupplier.get(supplier)
      return {
        supplier,
        lastPrice: entry?.price ?? null,
        ref: entry?.ref ?? null,
        date: entry?.date ?? null,
        stock: item.on_hand ?? null, // optional on `Item`, honestly — see `SupplierOption.stock` above
        hasHistory: !!entry,
      }
    })
    .sort((a, b) => {
      if (a.hasHistory !== b.hasHistory) return a.hasHistory ? -1 : 1
      if (a.hasHistory && b.hasHistory && a.date && b.date) return b.date.localeCompare(a.date)
      return a.supplier.localeCompare(b.supplier)
    })
}

export const openRequests = (requests: RevisionRequest[]): RevisionRequest[] =>
  requests.filter((r) => r.status === 'open')

/**
 * The requests still blocking a re-issue — server-side `open`, minus any the purchaser has
 * resolved in this session.
 *
 * ---- Why the session set is needed at all ----
 *
 * A `reprice` request is resolved by `update_quotation_line`, which writes to the QUOTATION and
 * never touches the request. And there is no API to close one: `get_revisions`,
 * `log_revision_request` and `apply_revisions` are the only three revision methods in the Aug 31
 * collection. So a repriced request stayed `open` for ever, and the send gate refused the re-issue
 * with "Resolve 1 open request(s) first." — to a purchaser who had just done exactly that.
 *
 * The set is SESSION-ONLY and does not survive a reload, which is the honest limit of a client-side
 * answer to a missing endpoint. Asked for as `D30`: a `close_revision_request(request)` method, or
 * for `update_quotation_line` to close the reprice request it satisfies.
 */
export const stillOpen = (
  requests: RevisionRequest[],
  resolvedHere: ReadonlySet<string>,
): RevisionRequest[] => openRequests(requests).filter((r) => !resolvedHere.has(r.id))
